(() => {
  const customConfig = window.SmikkelsFreeGiftRingsConfig || {};
  const config = {
    eligibleProductHandles: [
      'rvs-fruitbakje-280ml',
      'rvs-fruitbakje-420ml',
      'set-rvs-fruitbakjes',
      'complete-startset-basisschool-blauw',
      'complete-startset-basisschool-geel',
      'complete-startset-basisschool-groen',
      'complete-startset-basisschool-roze',
    ],
    giftProductHandle: 'set-van-2-siliconen-ringen',
    giftVariantId: null,
    giftPropertyName: '_free_gift_rings',
    giftPropertyValue: 'true',
    ...customConfig,
  };

  const state = {
    syncing: false,
    pending: false,
    giftVariantId: null,
    refreshTimer: null,
    syncPromise: null,
  };

  const getRoot = () => window.Shopify?.routes?.root || '/';
  const buildUrl = (path) => `${getRoot()}${path}`.replace(/\/{2,}/g, '/');

  const fetchJson = (url, options = {}) =>
    fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    }).then((response) => {
      if (!response.ok) throw new Error(`Free gift request failed: ${response.status}`);
      return response.json();
    });

  const getItemProperty = (item, name) => {
    if (!item.properties) return undefined;
    return item.properties[name];
  };

  const isGiftItem = (item, giftVariantId) =>
    Number(item.variant_id) === Number(giftVariantId) &&
    String(getItemProperty(item, config.giftPropertyName)) === config.giftPropertyValue;

  const isEligibleItem = (item) => {
    const handle = item.handle || (item.url || '').split('/products/')[1]?.split('?')[0]?.split('/')[0];
    return config.eligibleProductHandles.includes(handle);
  };

  const getGiftVariantId = async () => {
    if (config.giftVariantId) return Number(config.giftVariantId);
    if (state.giftVariantId) return state.giftVariantId;

    const product = await fetchJson(buildUrl(`products/${config.giftProductHandle}.js`));
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];

    if (!variant) throw new Error('No variant found for the free gift product.');

    state.giftVariantId = Number(variant.id);
    return state.giftVariantId;
  };

  const getCart = () => fetchJson(buildUrl('cart.js'));

  const addGift = (variantId, quantity) =>
    fetchJson(buildUrl('cart/add.js'), {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
            properties: {
              [config.giftPropertyName]: config.giftPropertyValue,
              _gift_label: 'Gratis reserve ringen',
            },
          },
        ],
      }),
    });

  const changeLine = (line, quantity) =>
    fetchJson(buildUrl('cart/change.js'), {
      method: 'POST',
      body: JSON.stringify({ line, quantity }),
    });

  const getSectionsToRender = () => {
    const sections = [];

    if (document.getElementById('CartDrawer')) sections.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
    if (document.getElementById('main-cart-items')) sections.push('main-cart-items');
    if (document.getElementById('main-cart-footer')) sections.push('main-cart-footer');

    return sections;
  };

  const replaceSection = (html, selectors) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    selectors.forEach(([targetSelector, sourceSelector = targetSelector]) => {
      const target = document.querySelector(targetSelector);
      const source = parsed.querySelector(sourceSelector);

      if (target && source) target.innerHTML = source.innerHTML;
    });
  };

  const refreshCartUi = async () => {
    const sections = getSectionsToRender();
    if (!sections.length) return;

    const url = `${buildUrl('cart')}?sections=${sections.join(',')}&sections_url=${encodeURIComponent(
      window.location.pathname
    )}`;
    const htmlBySection = await fetchJson(url);

    if (htmlBySection['cart-drawer']) {
      replaceSection(htmlBySection['cart-drawer'], [
        ['#CartDrawer', '#CartDrawer'],
        ['cart-drawer', 'cart-drawer'],
      ]);
    }

    if (htmlBySection['cart-icon-bubble']) {
      replaceSection(htmlBySection['cart-icon-bubble'], [['#cart-icon-bubble', '#cart-icon-bubble']]);
    }

    if (htmlBySection['main-cart-items']) {
      replaceSection(htmlBySection['main-cart-items'], [['#main-cart-items', '#main-cart-items']]);
    }

    if (htmlBySection['main-cart-footer']) {
      replaceSection(htmlBySection['main-cart-footer'], [['#main-cart-footer', '#main-cart-footer']]);
    }
  };

  const scheduleRefresh = () => {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(() => {
      refreshCartUi().catch((error) => console.error(error));
    }, 150);
  };

  const syncGift = async ({ refresh = true } = {}) => {
    if (state.syncing) {
      state.pending = true;
      return state.syncPromise;
    }

    state.syncing = true;
    state.syncPromise = (async () => {
      let changed = false;

      try {
        const [cart, giftVariantId] = await Promise.all([getCart(), getGiftVariantId()]);
        const giftItems = [];
        let desiredGiftQuantity = 0;

        cart.items.forEach((item, index) => {
          if (isGiftItem(item, giftVariantId)) {
            giftItems.push({ ...item, line: index + 1 });
            return;
          }

          if (isEligibleItem(item)) desiredGiftQuantity += item.quantity;
        });

        const currentGiftQuantity = giftItems.reduce((total, item) => total + item.quantity, 0);

        if (desiredGiftQuantity === 0) {
          for (const item of [...giftItems].reverse()) {
            await changeLine(item.line, 0);
            changed = true;
          }
        } else if (!giftItems.length) {
          await addGift(giftVariantId, desiredGiftQuantity);
          changed = true;
        } else {
          const [primaryGift, ...extraGifts] = giftItems;

          for (const item of [...extraGifts].reverse()) {
            await changeLine(item.line, 0);
            changed = true;
          }

          if (currentGiftQuantity !== desiredGiftQuantity || extraGifts.length) {
            await changeLine(primaryGift.line, desiredGiftQuantity);
            changed = true;
          }
        }

        if (changed && refresh) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-rings' });
          } else {
            scheduleRefresh();
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        state.syncing = false;

        if (state.pending) {
          state.pending = false;
          await syncGift({ refresh });
        }
      }

      return { changed };
    })();

    return state.syncPromise;
  };

  const scheduleSync = () => {
    window.setTimeout(() => syncGift(), 350);
  };

  document.addEventListener('DOMContentLoaded', scheduleSync);
  document.addEventListener('change', (event) => {
    if (event.target.closest('cart-items, cart-drawer-items')) scheduleSync();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest('cart-remove-button, quantity-input')) scheduleSync();
  });

  document.addEventListener(
    'click',
    async (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      if (!checkoutButton || checkoutButton.dataset.freeGiftSynced === 'true') return;

      const form = checkoutButton.form || checkoutButton.closest('form');
      if (!form && checkoutButton.tagName !== 'A') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      checkoutButton.setAttribute('aria-disabled', 'true');
      checkoutButton.classList.add('loading');

      await syncGift({ refresh: true });

      checkoutButton.dataset.freeGiftSynced = 'true';
      checkoutButton.classList.remove('loading');
      checkoutButton.removeAttribute('aria-disabled');

      if (checkoutButton.tagName === 'A') {
        window.location.href = checkoutButton.href;
      } else if (form?.requestSubmit) {
        form.requestSubmit(checkoutButton);
      } else {
        form?.submit();
      }
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'free-gift-rings') scheduleSync();
    });
  }

  window.SmikkelsFreeGiftRings = {
    sync: syncGift,
  };
})();
